import { lazy } from "react";

export const Dashboard = lazy(() =>
  import("../views/home/dashboard/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);

export const Apps = lazy(() => import("../components/apps"));

export const Platform = lazy(() => import("../views/settings/platform/platform"));

export const Account = lazy(() => import("../views/account/account"));

export const SurveysPage = lazy(() =>
  import("../views/surveys/SurveysPage").then((module) => ({
    default: module.SurveysPage,
  })),
);

export const SurveyBuilderPage = lazy(() =>
  import("../views/surveys/builder/SurveyBuilderPage").then((module) => ({
    default: module.SurveyBuilderPage,
  })),
);

export const SurveyDetailPage = lazy(() =>
  import("../views/surveys/detail/SurveyDetailPage").then((module) => ({
    default: module.SurveyDetailPage,
  })),
);

export const SurveyPreviewPage = lazy(() =>
  import("../views/surveys/preview/SurveyPreviewPage").then((module) => ({
    default: module.SurveyPreviewPage,
  })),
);

export const SurveyTemplatesPage = lazy(() =>
  import("../views/surveys/templates/SurveyTemplatesPage").then((module) => ({
    default: module.SurveyTemplatesPage,
  })),
);

export const PublicSurveyPage = lazy(() =>
  import("../views/surveys/public/PublicSurveyPage").then((module) => ({
    default: module.PublicSurveyPage,
  })),
);

export const TenantsSettings = lazy(() => import("../views/settings/tenants/tenants"));

export const InstanceProductsPage = lazy(
  () => import("../views/settings/products/InstanceProductsPage"),
);

export const CreateTenant = lazy(
  () => import("../views/settings/tenants/createTenant/createTenant"),
);

export const LogsPage = lazy(() => import("../views/settings/logs/LogsPage"));

export const UsersPage = lazy(() => import("../views/settings/users/UsersPage"));

export const RoleManagementPage = lazy(() =>
  import("../views/settings/roles/RoleManagementPage").then((module) => ({
    default: module.RoleManagementPage,
  })),
);

export const ClientsManagementPage = lazy(() =>
  import("../views/settings/clients/ClientsManagement").then((module) => ({
    default: module.ClientsManagement,
  })),
);

export const ErrorPage = lazy(() => import("../components/shared/errorpage"));

/** Hire's tiled Settings page (2026-10-08 shell spec, D5), opened by the sidebar's Settings item. */
export const SettingsHubPage = lazy(() => import("../views/settings/hub/SettingsHubPage"));
/** Hire's tiled Templates page, opened by the sidebar's Templates item (Templates & Setup). */
export const TemplatesHubPage = lazy(() => import("../views/templates/hub/TemplatesHubPage"));
export const GeneralSettingsPage = lazy(() =>
  import("../views/settings/general/GeneralSettingsPage").then((module) => ({
    default: module.GeneralSettingsPage,
  })),
);

export const LocationsPage = lazy(() =>
  import("../views/settings/locations/LocationsPage").then((module) => ({
    default: module.LocationsPage,
  })),
);

export const LeadsPage = lazy(() =>
  import("../views/leads/LeadsPage").then((module) => ({
    default: module.LeadsPage,
  })),
);

export const RediscoveryPage = lazy(() =>
  import("../views/rediscovery/RediscoveryPage").then((module) => ({
    default: module.RediscoveryPage,
  })),
);

export const DocumentCentrePage = lazy(() =>
  import("../views/documentCentre/DocumentCentrePage").then((module) => ({
    default: module.DocumentCentrePage,
  })),
);

export const ScheduledToursPage = lazy(() =>
  import("../views/scheduled-tours/ScheduledToursPage").then((module) => ({
    default: module.ScheduledToursPage,
  })),
);

export const CrmIntegrationsPage = lazy(() =>
  import("../views/settings/CrmIntegrationsPage").then((module) => ({
    default: module.CrmIntegrationsPage,
  })),
);

export const OnboardDashboard = lazy(() =>
  import("../views/onboard/OnboardDashboard").then((module) => ({
    default: module.OnboardDashboard,
  })),
);

export const OfferTemplatesPage = lazy(() =>
  import("../views/onboard/OfferTemplatesPage").then((module) => ({
    default: module.OfferTemplatesPage,
  })),
);

export const SkyAgentPage = lazy(() =>
  import("../views/skyagent/SkyAgentChat").then((module) => ({
    default: module.SkyAgentChat,
  })),
);

// skyAgent and skyBrain surfaces. The Workspace above is the chat itself;
// these are the rest of the catalog in src/views/skyagent/surfaces.ts.
// The retired /skyagent/admin, /memory and /capabilities pages, and skyBrain's
// Session History, are gone —
// their routes now render the redirects in pages/legacyRedirects.tsx, which
// are imported directly rather than lazily (they render one <Navigate>).
export const SkyAgentAIEmployeesPage = lazy(() =>
  import("../views/skyagent/pages/SkyAgentAIEmployeesPage").then((module) => ({
    default: module.SkyAgentAIEmployeesPage,
  })),
);

export const SkyAgentArtifactsPage = lazy(() =>
  import("../views/skyagent/pages/SkyAgentArtifactsPage").then((module) => ({
    default: module.SkyAgentArtifactsPage,
  })),
);

export const SkyAgentApprovalsPage = lazy(() =>
  import("../views/skyagent/pages/SkyAgentApprovalsPage").then((module) => ({
    default: module.SkyAgentApprovalsPage,
  })),
);

export const SkyAgentConfigurationPage = lazy(() =>
  import("../views/skyagent/pages/SkyAgentConfigurationPage").then((module) => ({
    default: module.SkyAgentConfigurationPage,
  })),
);

export const SkyBrainOverviewPage = lazy(() =>
  import("../views/skyagent/pages/SkyBrainOverviewPage").then((module) => ({
    default: module.SkyBrainOverviewPage,
  })),
);

export const SkyBrainMemoryPage = lazy(() =>
  import("../views/skyagent/pages/SkyBrainMemoryPage").then((module) => ({
    default: module.SkyBrainMemoryPage,
  })),
);

export const SkyBrainSkillsPage = lazy(() =>
  import("../views/skyagent/pages/SkyBrainSkillsPage").then((module) => ({
    default: module.SkyBrainSkillsPage,
  })),
);

export const SkyBrainQuickPromptsPage = lazy(() =>
  import("../views/skyagent/pages/SkyBrainQuickPromptsPage").then((module) => ({
    default: module.SkyBrainQuickPromptsPage,
  })),
);

export const JobPortalsPage = lazy(() =>
  import("../views/settings/jobPortals/JobPortalsPage").then((module) => ({
    default: module.JobPortalsPage,
  })),
);

export const JobPortalEditPage = lazy(() =>
  import("../views/settings/jobPortals/JobPortalEditPage").then((module) => ({
    default: module.JobPortalEditPage,
  })),
);

export const JobsPage = lazy(() =>
  import("../views/dashboard/jobs/JobsPage").then((module) => ({
    default: module.JobsPage,
  })),
);

export const JobDetailPage = lazy(() =>
  import("../views/dashboard/jobs/JobDetailPage").then((module) => ({
    default: module.JobDetailPage,
  })),
);

export const EmailTemplatesPage = lazy(() =>
  import("../views/templates/EmailTemplatesPage").then((module) => ({
    default: module.EmailTemplatesPage,
  })),
);

export const SmsTemplatesPage = lazy(() =>
  import("../views/templates/SmsTemplatesPage").then((module) => ({
    default: module.SmsTemplatesPage,
  })),
);

export const EmailSignaturePage = lazy(() =>
  import("../views/settings/EmailSignaturePage").then((module) => ({
    default: module.EmailSignaturePage,
  })),
);

export const SenderEmailsPage = lazy(() =>
  import("../views/settings/SenderEmailsPage").then((module) => ({
    default: module.SenderEmailsPage,
  })),
);

export const FeedbackTemplatesPage = lazy(() =>
  import("../views/feedback-templates/FeedbackTemplatesPage").then((module) => ({
    default: module.FeedbackTemplatesPage,
  })),
);

export const AiInterviewTemplatesPage = lazy(() =>
  import("../views/ai-interview-templates/AiInterviewTemplatesPage").then((module) => ({
    default: module.AiInterviewTemplatesPage,
  })),
);

export const AiInterviewTemplateEditorPage = lazy(() =>
  import("../views/ai-interview-templates/AiInterviewTemplateEditorPage").then((module) => ({
    default: module.AiInterviewTemplateEditorPage,
  })),
);

export const InterviewGuidesPage = lazy(() =>
  import("../views/interview-guides/InterviewGuidesPage").then((module) => ({
    default: module.InterviewGuidesPage,
  })),
);

export const InterviewGuideEditorPage = lazy(() =>
  import("../views/interview-guides/InterviewGuideEditorPage").then((module) => ({
    default: module.InterviewGuideEditorPage,
  })),
);

export const FeedbackBuilderPage = lazy(() =>
  import("../views/feedback-templates/builder/FeedbackBuilderPage").then((module) => ({
    default: module.FeedbackBuilderPage,
  })),
);

export const FeedbackSubmissionPage = lazy(() =>
  import("../views/leads/FeedbackSubmissionPage").then((module) => ({
    default: module.FeedbackSubmissionPage,
  })),
);

export const AiScoringPage = lazy(() => import("../views/settings/aiScoring/AiScoringPage"));

export const PipelineSettingsPage = lazy(
  () => import("../views/settings/pipeline/PipelineSettingsPage"),
);

export const ArchiveReasonsSettingsPage = lazy(
  () => import("../views/settings/archiveReasons/ArchiveReasonsSettingsPage"),
);

// Settings and Rules are two tabs of one page; both routes resolve here.
export const CandidateEngagementPage = lazy(
  () => import("../views/settings/candidateEngagement/CandidateEngagementPage"),
);

export const JobPostingOptionsSettingsPage = lazy(() =>
  import("../views/settings/jobPostingOptions/JobPostingOptionsSettingsPage").then((module) => ({
    default: module.JobPostingOptionsSettingsPage,
  })),
);

export const DepartmentsSettingsPage = lazy(() =>
  import("../views/settings/departments/DepartmentsSettingsPage").then((module) => ({
    default: module.DepartmentsSettingsPage,
  })),
);

export const JobTitlesSettingsPage = lazy(() =>
  import("../views/settings/jobTitles/JobTitlesSettingsPage").then((module) => ({
    default: module.JobTitlesSettingsPage,
  })),
);

export const IndeedIntegrationPage = lazy(() =>
  import("../views/settings/IndeedIntegrationPage").then((module) => ({
    default: module.IndeedIntegrationPage,
  })),
);

export const LinkedInIntegrationPage = lazy(() =>
  import("../views/settings/LinkedInIntegrationPage").then((module) => ({
    default: module.LinkedInIntegrationPage,
  })),
);

export const HrisIntegrationPage = lazy(() =>
  import("../views/settings/hris/HrisIntegrationPage").then((module) => ({
    default: module.HrisIntegrationPage,
  })),
);

// One HR system page (roster in + new hires out). /greythr-integration redirects into it.
export const HrSystemPage = lazy(() =>
  import("../views/settings/hrSystem/HrSystemPage").then((module) => ({
    default: module.HrSystemPage,
  })),
);

export const GreythrReviewPage = lazy(() =>
  import("../views/greythrReview/GreythrReviewPage").then((module) => ({
    default: module.GreythrReviewPage,
  })),
);
export const GreythrRedirect = lazy(() =>
  import("../views/settings/hrSystem/GreythrRedirect").then((module) => ({
    default: module.GreythrRedirect,
  })),
);

export const HelpPlusSupportPage = lazy(() =>
  import("../views/helpPlusSupport/helpPlusSupport").then((module) => ({
    default: module.HelpPlusSupport,
  })),
);

export const CandidateContactInfoPage = lazy(() =>
  import("../views/settings/candidateContactInfo/CandidateContactInfoPage").then((module) => ({
    default: module.CandidateContactInfoPage,
  })),
);

export const ResumeDigestPage = lazy(() =>
  import("../views/settings/resumeDigest/ResumeDigestPage").then((module) => ({
    default: module.ResumeDigestPage,
  })),
);

export const ResumeTemplatesPage = lazy(() =>
  import("../views/settings/resumeTemplates/ResumeTemplatesPage").then((module) => ({
    default: module.ResumeTemplatesPage,
  })),
);

export const AnonymizationConfigsPage = lazy(() =>
  import("../views/settings/anonymizationConfigs/AnonymizationConfigsPage").then((module) => ({
    default: module.AnonymizationConfigsPage,
  })),
);

export const ReportsPage = lazy(() =>
  import("../views/reports/ReportsPage").then((module) => ({
    default: module.ReportsPage,
  })),
);

export const BulkUploadsPage = lazy(() =>
  import("../views/bulk-uploads/BulkUploadsPage").then((module) => ({
    default: module.BulkUploadsPage,
  })),
);

export const OffersListPage = lazy(() =>
  import("../views/offers/OffersListPage").then((module) => ({
    default: module.OffersListPage,
  })),
);

export const OfferBuilderPage = lazy(() =>
  import("../views/offers/OfferBuilderPage").then((module) => ({
    default: module.OfferBuilderPage,
  })),
);

export const OfferTemplateListPage = lazy(() =>
  import("../views/offers/templates/OfferTemplateListPage").then((module) => ({
    default: module.OfferTemplateListPage,
  })),
);

export const OfferTemplateBuilderPage = lazy(() =>
  import("../views/offers/templates/OfferTemplateBuilderPage").then((module) => ({
    default: module.OfferTemplateBuilderPage,
  })),
);

export const ApprovalWorkflowPage = lazy(() =>
  import("../views/offers/workflows/ApprovalWorkflowPage").then((module) => ({
    default: module.ApprovalWorkflowPage,
  })),
);

export const ApprovalDashboardPage = lazy(() =>
  import("../views/offers/approvals/ApprovalDashboardPage").then((module) => ({
    default: module.ApprovalDashboardPage,
  })),
);

export const OfferAnalyticsDashboard = lazy(() =>
  import("../views/offers/analytics/OfferAnalyticsDashboard").then((module) => ({
    default: module.OfferAnalyticsDashboard,
  })),
);

export const CandidateInboxPage = lazy(() =>
  import("../views/candidate-inbox/CandidateInboxPage").then((module) => ({
    default: module.CandidateInboxPage,
  })),
);

// Employee referral hub
export const ReferralHubOverview = lazy(() =>
  import("../views/referralHub/ReferralHubOverview").then((module) => ({
    default: module.ReferralHubOverview,
  })),
);

export const OpenRolesPage = lazy(() =>
  import("../views/referralHub/OpenRolesPage").then((module) => ({
    default: module.OpenRolesPage,
  })),
);

export const MyReferralsPage = lazy(() =>
  import("../views/referralHub/MyReferralsPage").then((module) => ({
    default: module.MyReferralsPage,
  })),
);

export const RewardsPage = lazy(() =>
  import("../views/referralHub/RewardsPage").then((module) => ({
    default: module.RewardsPage,
  })),
);

/** Decides what `/` renders based on the signed-in user's role. */
export const HomeRoute = lazy(() =>
  import("./HomeRoute").then((module) => ({ default: module.HomeRoute })),
);
export const ConnectorsPage = lazy(() =>
  import("../views/settings/connectors/ConnectorsPage").then((module) => ({
    default: module.ConnectorsPage,
  })),
);

/**
 * The skyPerformance landing. `src/routes/**` is the one hire place allowed to lazy-load a
 * performance view (the import-boundary contract pins it).
 */
export const PerformanceHome = lazy(() => import("../products/performance/views/PerformanceHome"));
// skyPerformance IT (spec 2026-10-26 §4.6): its own lazy subtree, never in a hire or performance chunk.
export const PerformanceItHome = lazy(
  () => import("../products/performance-it/views/home/PerformanceItHome"),
);
/**
 * skyPerformance Settings (E9): one tabbed page, Roles and wording, <coaching> types, System of
 * record, Retention and Positions; each tab is a path segment.
 */
export const PerformanceSettingsPage = lazy(
  () => import("../products/performance/views/settings/PerformanceSettingsPage"),
);
/** skyPerformance's tiled Settings page (D5), at the bare `/performance/settings`. */
export const PerformanceSettingsHub = lazy(
  () => import("../products/performance/views/settings/PerformanceSettingsHub"),
);
/** skyPerformance E1: the coaching log ("My coaching" for an employee) and one record. */
export const CoachingLogPage = lazy(
  () => import("../products/performance/views/coaching/CoachingLogPage"),
);
export const CoachingRecordPage = lazy(
  () => import("../products/performance/views/coaching/CoachingRecordPage"),
);
/** skyPerformance E2: My file, and a person's file. */
export const PersonFilePage = lazy(
  () => import("../products/performance/views/file/PersonFilePage"),
);
/** skyPerformance E7: the organisation chart. */
export const OrgChartPage = lazy(() => import("../products/performance/views/org/OrgChartPage"));
/** skyPerformance E6: the to-do list (My to-dos for an employee). */
export const TodosPage = lazy(() => import("../products/performance/views/todos/TodosPage"));
/** skyPerformance E3: improvement plans (managers and HR) and one plan. */
export const PlansPage = lazy(() => import("../products/performance/views/plans/PlansPage"));
// E4: annual evaluations.
export const EvaluationsPage = lazy(
  () => import("../products/performance/views/evaluations/EvaluationsPage"),
);
// E5: the site-visit list and one visit.
export const VisitsPage = lazy(() => import("../products/performance/views/visits/VisitsPage"));
export const VisitPage = lazy(() => import("../products/performance/views/visits/VisitPage"));
// E8: Records and the Document Centre.
export const RecordsPage = lazy(() => import("../products/performance/views/records/RecordsPage"));
export const DocumentsPage = lazy(
  () => import("../products/performance/views/documents/DocumentsPage"),
);
export const EvaluationPage = lazy(
  () => import("../products/performance/views/evaluations/EvaluationPage"),
);
// E4.b: the employee's self-evaluation.
export const SelfEvaluationPage = lazy(
  () => import("../products/performance/views/evaluations/SelfEvaluationPage"),
);
export const PlanPage = lazy(() => import("../products/performance/views/plans/PlanPage"));
export const MyPlanPage = lazy(() => import("../products/performance/views/plans/MyPlanPage"));
// The Performance header search (wireframe index.html:45-48): the header reaches it only here.
export const PerformanceSearch = lazy(
  () => import("../products/performance/views/search/PerformanceSearch"),
);
// E10: the bell, beside the Performance search.
export const PerformanceNotifications = lazy(
  () => import("../products/performance/views/notifications/PerformanceNotifications"),
);
